import { useEffect, useState } from 'react'
import { API_BASE } from '../../constants'
import { normalizeProduct } from '../../normalizers'
import type { CatalogPagination } from '../../types'
import type { CatalogProduct } from '../../types'
import { fetchWithPolicy } from '../../../shared/api/fetchPolicy'
import { DEFAULT_PAGE_SIZE, type NumericFilterState, type OptionFilterState, type PageSize, type SearchFacets, type SearchSort } from './types'

const DEFAULT_PAGINATION: CatalogPagination = {
  page: 1,
  limit: DEFAULT_PAGE_SIZE,
  total: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPreviousPage: false,
}

export const useSearchProducts = ({
  searchTerm,
  page,
  pageSize,
  language,
  selectedBrands,
  selectedTypes,
  priceRange,
  numericFilters,
  optionFilters,
  sort,
}: {
  searchTerm: string
  page: number
  pageSize: PageSize
  language: 'it' | 'en'
  selectedBrands: string[]
  selectedTypes: string[]
  priceRange: { min: string; max: string }
  numericFilters: NumericFilterState
  optionFilters: OptionFilterState
  sort: SearchSort
}) => {
  const [products, setProducts] = useState<CatalogProduct[]>([])
  const [pagination, setPagination] = useState<CatalogPagination>(DEFAULT_PAGINATION)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [facets, setFacets] = useState<SearchFacets | null>(null)

  useEffect(() => {
    const trimmed = searchTerm.trim()
    if (!trimmed) {
      setProducts([])
      setPagination(DEFAULT_PAGINATION)
      setLoading(false)
      setError('')
      setFacets(null)
      return
    }

    const controller = new AbortController()
    const loadProducts = async () => {
      setLoading(true)
      setError('')
      try {
        const [sortBy, sortOrder] = sort.split('-')
        const params = new URLSearchParams({
          status: 'active',
          search: trimmed,
          page: String(page),
          limit: String(pageSize),
          sortBy: sortBy === 'updated' ? 'updatedAt' : sortBy,
          sortOrder,
        })
        selectedBrands.forEach((value) => params.append('brand', value))
        selectedTypes.forEach((value) => params.append('type', value))
        if (priceRange.min.trim()) params.set('price_min', priceRange.min.trim())
        if (priceRange.max.trim()) params.set('price_max', priceRange.max.trim())
        Object.entries(numericFilters).forEach(([id, range]) => {
          if (range.min.trim()) params.set(`nmin:${id}`, range.min.trim())
          if (range.max.trim()) params.set(`nmax:${id}`, range.max.trim())
        })
        Object.entries(optionFilters).forEach(([id, values]) => values.forEach((value) => params.append(`of:${id}`, value)))
        const response = await fetchWithPolicy({
          request: `${API_BASE}/catalog/products?${params.toString()}`,
          init: {
            signal: controller.signal,
          },
        })
        if (!response.ok) {
          throw new Error(language === 'en' ? 'Unable to load search results' : 'Impossibile caricare i risultati')
        }
        const payload = (await response.json()) as { products?: Record<string, unknown>[]; facets?: SearchFacets }
        const normalized = Array.isArray(payload.products)
          ? payload.products
              .map((entry) => normalizeProduct(entry))
              .filter((entry) => entry._id && entry.slug && (entry.status ?? 'active') === 'active')
          : []
        const rawPagination =
          payload && typeof payload === 'object' && 'pagination' in payload
            ? (payload as { pagination?: unknown }).pagination
            : undefined
        const nextPagination =
          rawPagination && typeof rawPagination === 'object'
            ? {
                page: Number((rawPagination as { page?: unknown }).page ?? page) || page,
                limit: Number((rawPagination as { limit?: unknown }).limit ?? pageSize) || pageSize,
                total: Number((rawPagination as { total?: unknown }).total ?? normalized.length) || normalized.length,
                totalPages:
                  Number((rawPagination as { totalPages?: unknown }).totalPages ?? 1) || 1,
                hasNextPage: Boolean((rawPagination as { hasNextPage?: unknown }).hasNextPage),
                hasPreviousPage: Boolean(
                  (rawPagination as { hasPreviousPage?: unknown }).hasPreviousPage,
                ),
              }
            : {
                page,
                limit: pageSize,
                total: normalized.length,
                totalPages: 1,
                hasNextPage: false,
                hasPreviousPage: false,
              }
        setProducts(normalized)
        setPagination(nextPagination)
        setFacets(payload.facets ?? null)
      } catch (nextError) {
        if (controller.signal.aborted) return
        setError(
          nextError instanceof Error && nextError.message.trim()
            ? nextError.message
            : language === 'en'
            ? 'Unable to load search results'
            : 'Impossibile caricare i risultati',
        )
        setProducts([])
        setPagination(DEFAULT_PAGINATION)
        setFacets(null)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    void loadProducts()
    return () => controller.abort()
  }, [language, numericFilters, optionFilters, page, pageSize, priceRange, searchTerm, selectedBrands, selectedTypes, sort])

  return {
    products,
    pagination,
    loading,
    error,
    facets,
  }
}
